import { useId, useState } from 'react'
import { Mail } from 'lucide-react'

import { Button } from 'components/ui/button'
import { Input } from 'components/ui/input'

/**
 * The gap an invitation leaves (MYD-623): Ask MDT never guesses an address, so
 * someone it could not match among the user's partners waits here for one.
 */

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export interface EmailPromptProps {
	name: string
	value?: string | null
	disabled?: boolean
	onSave: (email: string) => void
}

export function EmailPrompt({ name, value, disabled, onSave }: EmailPromptProps) {
	const [draft, setDraft] = useState(value ?? '')
	const [error, setError] = useState<string | null>(null)
	const id = useId()

	const save = () => {
		const email = draft.trim()
		if (!EMAIL.test(email)) {
			setError('Enter an email address like name@company.com.')
			return
		}
		setError(null)
		if (email !== value) onSave(email)
	}

	return (
		<form
			// Our check and our wording, not the browser's bubble, which differs
			// by browser and reads poorly in a side panel.
			noValidate
			className="mt-2 space-y-1.5 rounded-md border border-warning/50 bg-warning/10 p-2.5"
			onSubmit={event => {
				event.preventDefault()
				save()
			}}
		>
			<label htmlFor={id} className="flex items-start gap-1.5 text-sm text-gray-700">
				<Mail className="mt-0.5 h-3.5 w-3.5 shrink-0 text-gray-500" aria-hidden="true" />
				<span>
					<strong className="font-medium">{name}</strong> isn't one of your partners. Enter an email to invite them.
				</span>
			</label>
			<div className="flex gap-2">
				<Input
					id={id}
					type="email"
					inputMode="email"
					autoComplete="off"
					value={draft}
					disabled={disabled}
					onChange={event => setDraft(event.target.value)}
					placeholder="name@company.com"
					aria-invalid={Boolean(error)}
					aria-describedby={error ? `${id}-error` : undefined}
					className="h-8 flex-1"
				/>
				<Button type="submit" disabled={disabled || !draft.trim()} className="h-8">
					Save
				</Button>
			</div>
			{error && (
				<p id={`${id}-error`} className="text-xs text-destructive">
					{error}
				</p>
			)}
		</form>
	)
}

export default EmailPrompt
